import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DatePicker } from '@gravity-ui/date-components';
import { dateTime } from '@gravity-ui/date-utils';
import {
  Button,
  Card,
  Checkbox,
  Flex,
  Icon,
  Table,
  Text,
  TextInput,
  useToaster,
  type TableColumnConfig,
} from '@gravity-ui/uikit';
import { ArrowDownToLine, ChevronLeft, ChevronRight, CircleMinus, Plus } from '@gravity-ui/icons';
import { useApi } from '@/shared/api/use-api';
import { errorText } from '@/shared/api/error-text';
import {
  EmptyState,
  ErrorState,
  LoadingState,
  NoOrganizationSelectedState,
} from '@/shared/ui/states';
import { config } from '@/shared/config';
import { useObjectSelection } from '@/shared/object-selection';
import { useObjects } from '@/features/objects/api';
import { useProducts } from '@/features/products/api';
import { formatMoney, formatQuantity, parseDecimal } from '@/features/warehouse/format';
import { objectOptions, ObjectSelect } from '@/features/warehouse/object-select';
import { useTranslation } from '@/shared/i18n';
import { useBuffetAssortment, useBuffetDay, useSaveBuffetAssortment } from './api';
import { BuffetMovementDialog } from './buffet-movement-dialog';
import { shiftIso, todayIso } from './dates';
import type { BuffetDemo } from './buffet-demo-data';
import type { Product } from '@/features/products/types';
import type { BuffetDayRow, BuffetMovementKind, BuffetMovementLine } from './types';
import './buffet-route.css';

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const PRICE_DECIMALS = 2;

interface BuffetTableRow extends BuffetDayRow {
  selected: boolean;
}

function SummaryCard({ label, value }: { label: string; value: string }) {
  return (
    <Card className="buffet-summary__card" view="filled" style={{ padding: 16 }}>
      <Flex direction="column" gap={1}>
        <Text variant="body-1" color="secondary">
          {label}
        </Text>
        <Text variant="header-2">{value}</Text>
      </Flex>
    </Card>
  );
}

/**
 * «Буфет»: продажи вне основного меню на выбранном объекте и дату.
 *
 * Меню и порционник не трогаем: буфет — отдельная точка продаж, а не приём
 * пищи. Товары берутся из общего справочника продуктов, цены продажи живут
 * только здесь.
 */
export function BuffetRoute() {
  const api = useApi();
  const { t } = useTranslation();
  const toaster = useToaster();
  const [searchParams, setSearchParams] = useSearchParams();
  const rawDate = searchParams.get('date');
  const date = ISO_DATE_RE.test(rawDate ?? '') ? (rawDate as string) : todayIso();

  const setDate = (next: string) => {
    // Параметр demo сохраняем: иначе смена даты выключит демо-режим на стенде.
    const params = new URLSearchParams(searchParams);
    if (next === todayIso()) params.delete('date');
    else params.set('date', next);
    setSearchParams(params, { replace: true });
  };

  // Демо-данные: в local включаются сами, ?demo=1 включает принудительно,
  // ?demo=0 выключает. В проде — никогда.
  const demoParam = searchParams.get('demo');
  const demo =
    config.env !== 'prod' && (demoParam === '1' || (demoParam !== '0' && config.env === 'local'));

  // Генератор демо-данных тянем отдельным чанком: в проде он не нужен вовсе.
  const [demoState, setDemoState] = useState<BuffetDemo | null>(null);
  const [demoFailed, setDemoFailed] = useState(false);
  useEffect(() => {
    if (!demo) {
      setDemoState(null);
      setDemoFailed(false);
      return;
    }

    let cancelled = false;
    void import('./buffet-demo-data')
      .then((module) => {
        if (!cancelled) setDemoState(module.createBuffetDemo());
      })
      .catch(() => {
        // Чанк не загрузился — показываем сообщение, а не вечную загрузку.
        if (!cancelled) setDemoFailed(true);
      });

    return () => {
      cancelled = true;
    };
  }, [demo]);

  const [objectId, setObjectId] = useState('');
  const objects = useObjects();
  const { objectId: savedObjectId, setObjectId: saveObjectId } = useObjectSelection();

  const realOptions = useMemo(() => objectOptions(objects.data?.objects ?? []), [objects.data]);
  const demoOptions = useMemo(
    () => (demoState?.objects ?? []).map((object) => ({ value: object.id, content: object.name })),
    [demoState]
  );
  const options = demo ? demoOptions : realOptions;

  useEffect(() => {
    if (options.length === 0) return;
    if (options.some((option) => option.value === objectId)) return;

    const preferred =
      !demo && savedObjectId && options.some((option) => option.value === savedObjectId)
        ? savedObjectId
        : options[0].value;
    setObjectId(preferred);
  }, [demo, objectId, options, savedObjectId]);

  const changeObjectId = (next: string) => {
    setObjectId(next);
    // Демо-объекты не запоминаем: иначе их id уедет на другие страницы.
    if (!demo) saveObjectId(next);
  };

  const day = useBuffetDay(objectId, date, !demo);
  const assortment = useBuffetAssortment(objectId, !demo);
  const products = useProducts();
  const saveAssortment = useSaveBuffetAssortment();

  const rows = useMemo(() => {
    if (demo) return demoState ? demoState.getDay(objectId, date) : [];
    return day.data?.rows ?? [];
  }, [demo, demoState, objectId, date, day.data]);

  const items = useMemo(() => {
    if (demo) return demoState ? demoState.getAssortment(objectId) : [];
    return assortment.data?.items ?? [];
  }, [demo, demoState, objectId, assortment.data]);

  const catalogProducts = useMemo(
    () =>
      (demo ? (demoState?.products ?? []) : (products.data?.products ?? [])).filter(
        (product) => !product.water
      ),
    [demo, demoState, products.data]
  );
  const [assortmentDraft, setAssortmentDraft] = useState<Record<string, string>>({});
  const [assortmentDraftObjectId, setAssortmentDraftObjectId] = useState('');
  useEffect(() => {
    setAssortmentDraft(
      Object.fromEntries(items.map((item) => [item.productId, String(item.salePrice)]))
    );
    setAssortmentDraftObjectId(objectId);
  }, [objectId, items]);
  const [movement, setMovement] = useState<BuffetMovementKind | null>(null);

  // Демо-состояние меняется в памяти. Новый объект-обёртка заставляет
  // пересчитать строки дня и ассортимент.
  const refreshDemo = useCallback(
    () => setDemoState((current) => (current ? { ...current } : current)),
    []
  );

  const submitDemoMovement = (payload: {
    kind: BuffetMovementKind;
    items: BuffetMovementLine[];
    reason: string;
  }) => {
    demoState?.addMovement(objectId, date, payload.kind, payload.items, payload.reason);
    refreshDemo();
  };

  const setProductSelected = (productId: string, checked: boolean) => {
    setAssortmentDraft((current) => {
      if (!checked) {
        const next = { ...current };
        delete next[productId];
        return next;
      }

      return { ...current, [productId]: current[productId] ?? '' };
    });
  };

  const setProductPrice = (productId: string, value: string) => {
    setAssortmentDraft((current) => ({
      ...current,
      [productId]: value.replace(/[^\d.,]/g, ''),
    }));
  };

  const selectedProductIds = Object.keys(assortmentDraft);
  const invalidPrices = selectedProductIds.filter((productId) => {
    const price = parseDecimal(assortmentDraft[productId] ?? '', PRICE_DECIMALS);
    return price === null || price <= 0;
  });
  const assortmentDirty =
    selectedProductIds.length !== items.length ||
    items.some((item) => assortmentDraft[item.productId] !== String(item.salePrice));
  const assortmentDraftKey = JSON.stringify(
    Object.entries(assortmentDraft).sort(([left], [right]) => left.localeCompare(right))
  );
  const lastAssortmentSave = useRef('');

  const saveInlineAssortment = useCallback(() => {
    if (!objectId || invalidPrices.length > 0 || !assortmentDirty) return;

    const next = selectedProductIds.map((productId) => ({
      productId,
      salePrice: parseDecimal(assortmentDraft[productId] ?? '', PRICE_DECIMALS) ?? 0,
    }));

    if (demo) {
      const productById = new Map(catalogProducts.map((product) => [product.id, product]));
      demoState?.setAssortment(
        objectId,
        next.map((item) => {
          const product = productById.get(item.productId);
          return {
            ...item,
            productName: product?.name ?? '',
            unit: product?.unit ?? '',
          };
        })
      );
      refreshDemo();
      toaster.add({
        name: 'buffet-assortment-saved',
        title: t('buffet.assortmentSaved'),
        theme: 'success',
      });
      return;
    }

    saveAssortment.mutate(
      { objectId, items: next },
      {
        onSuccess: () => {
          toaster.add({
            name: 'buffet-assortment-saved',
            title: t('buffet.assortmentSaved'),
            theme: 'success',
          });
        },
        onError: (error) =>
          toaster.add({
            name: 'buffet-assortment-error',
            title: t('common.error'),
            content: errorText(error),
            theme: 'danger',
          }),
      }
    );
  }, [
    assortmentDraft,
    assortmentDirty,
    catalogProducts,
    demo,
    demoState,
    invalidPrices.length,
    objectId,
    refreshDemo,
    saveAssortment,
    selectedProductIds,
    t,
    toaster,
  ]);

  useEffect(() => {
    if (
      !objectId ||
      assortmentDraftObjectId !== objectId ||
      !assortmentDirty ||
      invalidPrices.length > 0 ||
      (!demo && saveAssortment.isPending) ||
      lastAssortmentSave.current === assortmentDraftKey
    ) {
      return;
    }

    const timeout = window.setTimeout(() => {
      lastAssortmentSave.current = assortmentDraftKey;
      saveInlineAssortment();
    }, 500);

    return () => window.clearTimeout(timeout);
  }, [
    assortmentDraftKey,
    assortmentDraftObjectId,
    assortmentDirty,
    demo,
    invalidPrices.length,
    objectId,
    saveInlineAssortment,
    saveAssortment.isPending,
  ]);

  const tableRows = useMemo(() => {
    const productsById = new Map<string, Pick<Product, 'id' | 'name' | 'unit'>>();
    for (const product of catalogProducts) {
      productsById.set(product.id, product);
    }
    for (const item of items) {
      if (!productsById.has(item.productId)) {
        productsById.set(item.productId, {
          id: item.productId,
          name: item.productName,
          unit: item.unit,
        });
      }
    }
    for (const row of rows) {
      if (!productsById.has(row.productId)) {
        productsById.set(row.productId, {
          id: row.productId,
          name: row.productName,
          unit: row.unit,
        });
      }
    }

    const dayRowsById = new Map(rows.map((row) => [row.productId, row]));
    const itemsById = new Map(items.map((item) => [item.productId, item]));
    return Array.from(productsById.values()).map((product) => {
      const dayRow = dayRowsById.get(product.id);
      const item = itemsById.get(product.id);
      return {
        productId: product.id,
        productName: product.name,
        unit: product.unit,
        salePrice: dayRow?.salePrice ?? item?.salePrice ?? 0,
        costPrice: dayRow?.costPrice ?? 0,
        opening: dayRow?.opening ?? 0,
        received: dayRow?.received ?? 0,
        sold: dayRow?.sold ?? 0,
        writtenOff: dayRow?.writtenOff ?? 0,
        closing: dayRow?.closing ?? 0,
        selected: product.id in assortmentDraft,
      };
    });
  }, [assortmentDraft, catalogProducts, items, rows]);

  const totals = useMemo(
    () =>
      rows.reduce(
        (acc, row) => ({
          revenue: acc.revenue + row.sold * row.salePrice,
          cost: acc.cost + row.sold * row.costPrice,
          writeOffs: acc.writeOffs + row.writtenOff * row.costPrice,
        }),
        { revenue: 0, cost: 0, writeOffs: 0 }
      ),
    [rows]
  );
  const margin = totals.revenue - totals.cost;

  const columns: TableColumnConfig<BuffetTableRow>[] = [
    {
      id: 'selected',
      name: '',
      width: 44,
      template: (row) => (
        <Checkbox
          size="m"
          checked={row.selected}
          onUpdate={(checked) => setProductSelected(row.productId, checked)}
          aria-label={row.productName}
        />
      ),
    },
    {
      id: 'productName',
      name: t('buffet.colProduct'),
      template: (row) => (
        <Flex direction="column">
          <Text variant="body-2">{row.productName}</Text>
          <Text variant="caption-2" color="secondary">
            {row.unit}
          </Text>
        </Flex>
      ),
    },
    {
      id: 'salePrice',
      name: t('buffet.colPrice'),
      template: (row) => {
        const price = assortmentDraft[row.productId] ?? '';
        const parsedPrice = parseDecimal(price, PRICE_DECIMALS);
        const invalid = row.selected && (parsedPrice === null || parsedPrice <= 0);

        return (
          <TextInput
            size="l"
            value={price}
            onUpdate={(value) => setProductPrice(row.productId, value)}
            placeholder={t('buffet.salePricePlaceholder')}
            disabled={!row.selected}
            validationState={invalid ? 'invalid' : undefined}
            style={{ width: 120 }}
          />
        );
      },
    },
    {
      id: 'opening',
      name: t('buffet.colOpening'),
      align: 'end',
      template: (row) => <Text variant="body-2">{formatQuantity(row.opening, row.unit)}</Text>,
    },
    {
      id: 'received',
      name: t('buffet.colReceived'),
      align: 'end',
      template: (row) => (
        <Text variant="body-2" color="positive">
          {row.received > 0 ? `+${formatQuantity(row.received, row.unit)}` : '—'}
        </Text>
      ),
    },
    {
      id: 'sold',
      name: t('buffet.colSold'),
      align: 'end',
      template: (row) => <Text variant="body-2">{formatQuantity(row.sold, row.unit)}</Text>,
    },
    {
      id: 'writtenOff',
      name: t('buffet.colWrittenOff'),
      align: 'end',
      template: (row) => (
        <Text variant="body-2" color={row.writtenOff > 0 ? 'danger' : undefined}>
          {row.writtenOff > 0 ? formatQuantity(row.writtenOff, row.unit) : '—'}
        </Text>
      ),
    },
    {
      id: 'closing',
      name: t('buffet.colClosing'),
      align: 'end',
      template: (row) => <Text variant="body-2">{formatQuantity(row.closing, row.unit)}</Text>,
    },
    {
      id: 'revenue',
      name: t('buffet.colRevenue'),
      align: 'end',
      template: (row) => <Text variant="body-2">{formatMoney(row.sold * row.salePrice)}</Text>,
    },
  ];

  if (api.organizationId === null) {
    return (
      <Flex direction="column" className="buffet-page">
        <NoOrganizationSelectedState />
      </Flex>
    );
  }

  const demoLoading = demo && demoState === null && !demoFailed;
  const assortmentPending = !demo && assortment.isPending;
  const dayPending = !demo && day.isPending;
  const productsPending = !demo && products.isPending;
  const noObjects = demo
    ? demoState !== null && demoOptions.length === 0
    : objects.isSuccess && options.length === 0;
  const loading = demo
    ? demoLoading
    : objects.isPending ||
      productsPending ||
      (objectId !== '' && (day.isPending || assortment.isPending));
  const loadError = demo
    ? null
    : (objects.error ?? day.error ?? assortment.error ?? products.error);
  const reload = () => {
    void objects.refetch();
    void day.refetch();
    void assortment.refetch();
    void products.refetch();
  };

  return (
    <div className="buffet-page">
      <Flex className="buffet-topbar" alignItems="center" justifyContent="space-between" gap={3}>
        <Flex className="buffet-filters" alignItems="center" gap={2}>
          <ObjectSelect
            value={objectId}
            onUpdate={changeObjectId}
            options={options}
            disabled={!demo && objects.isPending}
          />
          <Flex className="buffet-filters__date" alignItems="center" gap={1}>
            <Button
              view="outlined"
              size="l"
              title={t('buffet.prevDay')}
              onClick={() => setDate(shiftIso(date, -1))}
            >
              <Icon data={ChevronLeft} size={16} />
            </Button>
            <DatePicker
              size="l"
              value={dateTime({ input: date })}
              onUpdate={(value) => setDate(value ? value.format('YYYY-MM-DD') : todayIso())}
              style={{ width: 180 }}
            />
            <Button
              view="outlined"
              size="l"
              title={t('buffet.nextDay')}
              onClick={() => setDate(shiftIso(date, 1))}
            >
              <Icon data={ChevronRight} size={16} />
            </Button>
          </Flex>
        </Flex>

        <Flex className="buffet-topbar__actions" alignItems="center" gap={2}>
          <Button
            view="outlined"
            size="l"
            onClick={() => setMovement('transfer')}
            disabled={!objectId || assortmentPending}
          >
            <Icon data={ArrowDownToLine} size={16} />
            {t('buffet.transfer')}
          </Button>
          <Button
            view="outlined"
            size="l"
            onClick={() => setMovement('writeOff')}
            disabled={!objectId || assortmentPending || dayPending}
          >
            <Icon data={CircleMinus} size={16} />
            {t('buffet.writeOff')}
          </Button>
          <Button
            view="action"
            size="l"
            onClick={() => setMovement('sale')}
            disabled={!objectId || assortmentPending || dayPending}
          >
            <Icon data={Plus} size={16} />
            {t('buffet.sale')}
          </Button>
        </Flex>
      </Flex>

      {demo && demoFailed && <EmptyState text={t('buffet.dataUnavailable')} />}

      {noObjects && <EmptyState text={t('buffet.emptyNoObjects')} />}
      {!noObjects && loading && <LoadingState />}
      {!demo && !noObjects && !loading && (loadError || objects.isError) && (
        <ErrorState error={loadError} onRetry={reload} />
      )}

      {!noObjects && !loading && !loadError && !demoFailed && (
        <>
          <Flex className="buffet-summary" gap={3} wrap>
            <SummaryCard label={t('buffet.revenue')} value={formatMoney(totals.revenue)} />
            <SummaryCard label={t('buffet.cost')} value={formatMoney(totals.cost)} />
            <SummaryCard label={t('buffet.margin')} value={formatMoney(margin)} />
            <SummaryCard label={t('buffet.writeOffs')} value={formatMoney(totals.writeOffs)} />
          </Flex>

          {catalogProducts.length === 0 && tableRows.length === 0 ? (
            <EmptyState text={t('buffet.noProducts')} />
          ) : (
            <Card view="filled" className="buffet-table-card" style={{ padding: 0 }}>
              <div className="buffet-table-wrap">
                <Table width="max" className="buffet-table" data={tableRows} columns={columns} />
              </div>
            </Card>
          )}
        </>
      )}

      {movement && (
        <BuffetMovementDialog
          open
          kind={movement}
          objectId={objectId}
          date={date}
          assortment={items}
          dayRows={rows}
          demo={demo && demoState ? { onSubmit: submitDemoMovement } : undefined}
          onClose={() => setMovement(null)}
        />
      )}
    </div>
  );
}
